import {Link, Navigate, Outlet, useLocation} from 'react-router-dom';
import {Badge, Layout, Menu} from 'antd';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import '../../assets/css/DesktopLayout.css';
import {SettingOutlined} from "@ant-design/icons";
import {menuItems} from "../../utils/utils";
import {ItemType} from 'antd/es/menu/hooks/useItems';
import {FormattedMessage} from "react-intl";
import {RightHeaderMenu} from "./RightHeaderMenu";
import {AlertContext} from "../../context/AlertContext";
import {HelpSectionContext} from "../../context/HelpSectionContext";
import {bitwiseOr, isAccessibleFor, isNotAccessibleFor} from "../../utils/accessUtils";
import {ALERT_DURATIONS} from "../../constants/alerts";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import CustomAlert from "./CustomAlert";
import DentobookLink from "../common/DentobookLink";


export const DesktopLayout = () => {

    const {principal, userOrganization} = useContext(DentobookApplicationContext);

    const [visible, setVisible] = useState<boolean>(false);
    const [error, setError] = useState<React.ReactNode>('');
    const [warning, setWarning] = useState<React.ReactNode>('');
    const [successMessage, setSuccessMessage] = useState<React.ReactNode>('');
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);


    const [menuItemsArr, setMenuItemsArr] = useState<Object[]>([{}]);
    const location = useLocation();

    const {Sider} = Layout;

    // Show warning banner if the interceptor switched orgs after membership revocation.
    useEffect(() => {
        const flash = sessionStorage.getItem('dentobook_session_event');
        if (!flash) return;
        try {
            const event = JSON.parse(flash);
            sessionStorage.removeItem('dentobook_session_event');
            if (event.type === 'org_switched_after_revoke') {
                setWarning(<FormattedMessage id="org_switched_after_revoke" values={{ orgName: event.orgName }} />);
                setTimeout(() => setWarning(''), ALERT_DURATIONS.STANDARD);
            }
        } catch { /* ignore malformed flash */ }
    }, []);

    useEffect(() => {
        const visibleItems = menuItems.filter(item => {
            if ((item.name === 'nzis' && userOrganization?.country !== 'BG')
                || (item.name === 'activity_log' && !principal?.features.includes('ACTIVITY_LOG'))
                || (item.name === 'reports_label' && !principal?.features.includes('REPORTING'))) {
                return false;
            }
            if (item.accessibleFor) {
                return isAccessibleFor(principal?.user, bitwiseOr(item.accessibleFor));
            }
            if (item.notAccessibleFor) {
                return isNotAccessibleFor(principal?.user, bitwiseOr(item.notAccessibleFor));
            }
            return true;
        })
        // Find the most specific visible path that is a prefix of (or exact match for)
        // the current location. This prevents a parent path like /patients from being
        // highlighted when a more specific child path like /patients/import is active.
        const activeMenuPath = visibleItems
            .filter(item =>
                item.path !== '/' &&
                (location.pathname === item.path || location.pathname.startsWith(item.path + '/'))
            )
            .sort((a, b) => b.path.length - a.path.length)[0]?.path ?? location.pathname;

        setMenuItemsArr(visibleItems.map((m) => {
            const cssClassOne = m.path === activeMenuPath ? 'ant-menu-item-selected' : '';

            return {
                key: m.path,
                className: cssClassOne,
                icon: m.bottom ? <SettingOutlined/> : null,
                label: (
                    <DentobookLink to={m.path}>
                        {m.name ? <FormattedMessage id={m.name}/> : m.path}
                        {(m as any).badge && (
                            <Badge
                                count="NEW"
                                style={{
                                    backgroundColor: '#1677ff',
                                    fontSize: 10,
                                    marginLeft: 6,
                                    verticalAlign: 'middle',
                                }}
                            />
                        )}
                    </DentobookLink>
                )
            }
        }));
    }, [location.pathname, principal, userOrganization]);

    const alertContextValue = useMemo(() => ({
        visible,
        setVisible,
        warning,
        setWarning,
        error,
        setError,
        successMessage,
        setSuccessMessage,
        redirectUrl,
        setRedirectUrl
    }), [visible, warning, error, successMessage, redirectUrl]);

    const [helpSlug, setHelpSlug] = useState<string | null>(null);
    const helpContextValue = useMemo(() => ({ helpSlug, setHelpSlug }), [helpSlug]);

    return (
        <AlertContext.Provider value={alertContextValue} /*NOSONAR*/ >
            <HelpSectionContext.Provider value={helpContextValue}>
            <Layout>
                <CustomAlert/>
                <nav className="nav">
                    <div>
                        <Link to="/">
                            <img alt="logo" src={require('../../assets/logo.png')} height={56} style={{ cursor: 'pointer' }} />
                        </Link>
                    </div>
                    <div>
                        <RightHeaderMenu/>
                    </div>
                </nav>
                <Layout>

                    <CustomAlert/>
                    {/*<ExitPageModal/>*/}
                    <nav className="nav">
                        <div>
                            <Link to="/">
                                <img alt="logo" src={require('../../assets/logo.png')} height={56} style={{ cursor: 'pointer' }} />
                            </Link>
                        </div>
                        <div>
                            <RightHeaderMenu/>
                        </div>
                    </nav>

                    <Layout>
                        <Sider width={262} className="sider-layout">
                            <Menu
                                mode="inline"
                                defaultSelectedKeys={['/']}
                                selectedKeys={[location.pathname]}
                                className="menu-layout"
                                items={(menuItemsArr as ItemType[])}
                            />
                        </Sider>
                        <Layout className="layout">

                            <Layout>
                                <div>
                                    <Outlet/>
                                </div>
                            </Layout>
                        </Layout>
                    </Layout>
                </Layout>
            </Layout>
            </HelpSectionContext.Provider>
        </AlertContext.Provider>
    )

};